/**
 * The permission vocabulary, mirrored from the API.
 *
 * The authoritative copy is `sabha.permissions` (migration 028) and
 * `core/authorization.py`. This list exists so a component can name a
 * permission without a string literal that a typo turns into a silent denial.
 *
 * A KEY THE SERVER SENDS AND THIS FILE DOES NOT KNOW IS STILL HONOURED --
 * `hasPermission` compares raw strings, so an older frontend against a newer
 * API keeps working and simply has no constant for the new capability. The
 * reverse (a constant here that the server never sends) reads as the permission
 * never being granted, which is the safe direction.
 */
export const PERMISSIONS = {
  meetingRead: 'meeting:read',
  meetingReadAll: 'meeting:read_all',
  transcriptRead: 'transcript:read',
  audioDownload: 'audio:download',
  meetingShare: 'meeting:share',
  meetingReprocess: 'meeting:reprocess',
  meetingDelete: 'meeting:delete',
  meetingTagPatron: 'meeting:tag_patron',
  meetingRecord: 'meeting:record',
  meetingScreenCapture: 'meeting:screen_capture',
  actionRead: 'action:read',
  actionWrite: 'action:write',
  actionDelete: 'action:delete',
  actionPush: 'action:push',
  searchRead: 'search:read',
  analyticsRead: 'analytics:read',
  askUse: 'ask:use',
  patronRead: 'patron:read',
  patronManage: 'patron:manage',
  workspaceRead: 'workspace:read',
  workspaceManage: 'workspace:manage',
  memberRead: 'member:read',
  memberManage: 'member:manage',
  roleManage: 'role:manage',
  ruleManage: 'rule:manage',
  languageRead: 'language:read',
  languageManage: 'language:manage',
  notificationManage: 'notification:manage',
  securityManage: 'security:manage',
  integrationRead: 'integration:read',
  integrationManage: 'integration:manage',
  auditRead: 'audit:read',
  billingRead: 'billing:read',
  exportRequest: 'export:request',
} as const

export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS]

/** A role as the workspace defined it. `permissions` is the whole of its meaning. */
export interface RoleSummary {
  id: string
  key: string
  name: string
  description: string | null
  permissions: string[]
  isSystem: boolean
  isProtected: boolean
  memberCount: number
  /**
   * Whether the CURRENT viewer may edit this row. Sent by the server rather
   * than derived here, because the Owner row in front of an admin holding
   * every other permission is not derivable from the flags -- protection is
   * "do you hold this role", which the browser cannot evaluate.
   */
  canEdit: boolean
  canDelete: boolean
}

export interface PermissionDescriptor {
  key: string
  category: string
  label: string
  description: string
}
